import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, Zap, Shield, BarChart3, Handshake, ArrowRight, CheckCircle } from 'lucide-react';

const benefits = [
  { icon: Target, title: 'Captive Audience', description: 'Renters need insurance. We show it at the right time.', gradient: 'from-red-500 to-orange-500' },
  { icon: TrendingUp, title: 'Low CAC', description: 'Reduce customer acquisition costs by 50-70%.', gradient: 'from-blue-500 to-cyan-500' },
  { icon: Zap, title: 'Embedded Products', description: 'Policies sold inside the app. No redirects. No drop-offs.', gradient: 'from-purple-500 to-pink-500' },
  { icon: Shield, title: 'Automated Claims', description: 'Digital submission reduces fraud and improves service.', gradient: 'from-emerald-500 to-green-500' },
  { icon: BarChart3, title: 'Analytics & Insights', description: 'Real-time data on conversion and claims.', gradient: 'from-cyan-500 to-blue-500' },
  { icon: Handshake, title: 'Revenue Sharing', description: 'Clear, mutual commission model with growth potential.', gradient: 'from-yellow-500 to-amber-500' },
];

const partnerBenefits = {
  you: [
    'Qualified lead generation',
    'Co-marketing opportunities',
    'Performance analytics',
    'Dedicated partner support',
  ],
  customers: [
    'Convenient in-app purchasing',
    'Instant policy issuance',
    'Easy claims management',
    'Competitive pricing',
    'Community recommendations',
  ],
};

const tiers = [
  {
    title: 'Integration Partner',
    features: ['API Access', 'Standard Support', 'Monthly Reports'],
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    title: 'Premier Partner',
    features: ['Co-Marketing', 'Priority Support', 'Weekly Reports', 'Custom Integration'],
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    title: 'Vendor Partner',
    features: ['List Your Products & Services', 'Reach 12M+ Renters', 'Direct Tenant Orders'],
    gradient: 'from-orange-500 to-red-500',
  },
];

export default function PartnersPage() {
  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-6">
            For Partners
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Reach Millions of{' '}
            <span className="text-gradient-warm">Qualified Renters.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            YAWOTE gives insurance companies direct access to a captive audience of 12M+ renters 
            with low friction and low acquisition costs.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, idx) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={idx}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                <div className="relative card">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="text-white" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Partnership Model */}
        <motion.div
          className="rounded-2xl p-12 mb-20 relative overflow-hidden"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/20 rounded-2xl"></div>
          <div className="relative">
            <h2 className="text-3xl font-bold text-white mb-8">Why Partner With YAWOTE</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-white mb-4">For You</h3>
                <ul className="space-y-3">
                  {partnerBenefits.you.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-gray-300">
                      <CheckCircle size={18} className="text-orange-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-4">For Your Customers</h3>
                <ul className="space-y-3">
                  {partnerBenefits.customers.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-gray-300">
                      <CheckCircle size={18} className="text-orange-400 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Partnership Tiers */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-white text-center mb-12">Partnership Models</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {tiers.map((tier, idx) => (
              <motion.div
                key={idx}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                viewport={{ once: true }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${tier.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
                <div className="relative card text-center">
                  <h3 className="text-2xl font-bold text-white mb-6">{tier.title}</h3>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="text-gray-300 text-sm">{feature}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Partner CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="rounded-2xl p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/20 rounded-2xl"></div>
            <div className="relative">
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Partner With Us?</h2>
              <p className="text-gray-300 mb-8 max-w-xl mx-auto">
                Fill in a short form and our team will get back to you within 24 hours.
              </p>
              <a href="/partner-form">
                <button className="py-4 px-10 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300 inline-flex items-center gap-2 group">
                  Apply to Partner
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </a>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
